import type { AppProps } from "next/app";
import Head from "next/head";
import MerakiSDK from "@urbanpiper-engineering/meraki-sdk";
import { Source_Sans_3 } from "next/font/google";
import { appWithTranslation } from "next-i18next";

import { Partytown } from "@builder.io/partytown/react";
import ThemeProvider from "@urbanpiper-engineering/meraki-components/dist/provider";
import Layout from "../components/layout";
import ErrorBoundary from "../utils/error";

import nextI18nConfig from "../next-i18next.config";
import configSelector from "../external/configSelector";

import pageSelector from "../external/pageSelector";
import { useEffect } from "react";
import { freshChatService, getAnalyticsOptions } from "../utils/analytics";

import "./global.scss";

const fonts = Source_Sans_3({
  variable: "--font-family",
  weight: ["400", "600", "700"],
  subsets: ["latin"],
  display: "swap",
  preload: true,
});

function MedusaApp(props: AppProps) {
  const { Component, pageProps } = props;
  const { businessInfo, configJSON, locale, theme } = pageProps;

  // Always update config if provided from server
  if (configJSON) {
    configSelector.updateConfig(configJSON);
    pageSelector.updateConfig(configJSON);
  }

  // CRITICAL: Always re-initialize SDK on every render to ensure correct API URL
  // The bundled root.config.json ensures we always have a valid config even without pageProps
  const sdkConfig = configSelector.getSDKConfig();

  if (sdkConfig?.url) {
    MerakiSDK.initSync(
      {
        ...sdkConfig,
        locale: locale || "en",
      },
      businessInfo || MerakiSDK.getBusinessInfo(),
    );
  }

  if (theme) {
    theme.fontFamily = fonts.style.fontFamily;
    theme.isStoreClosed = MerakiSDK.getStoreState().isStoreClosed || false;
  }

  useEffect(() => {
    const analyticsInitOptions = getAnalyticsOptions(theme);
    MerakiSDK.analyticsInit(analyticsInitOptions);
    freshChatService();
  }, []);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <Partytown forward={["fbq", "mixpanel.track"]} />

        <link
          rel="icon"
          type="image/x-icon"
          href={configSelector.getFavicon()}
        />
        <link rel="preconnect" href="https://accounts.google.com" />
        <link rel="preconnect" href="https://s3-ap-southeast-1.amazonaws.com" />
        <link rel="preconnect" href="https://meraki-api.svc.urbanpiper.com" />
        <link rel="preconnect" href="https://cdn.mxpnl.com" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        <link rel="preconnect" href="https://connect.facebook.net"></link>
      </Head>
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <Layout {...pageProps} className={fonts.className}>
            <Component {...pageProps} />
          </Layout>
        </ThemeProvider>
      </ErrorBoundary>
    </>
  );
}

export default appWithTranslation(MedusaApp, nextI18nConfig);
